import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Palette, Upload, Save, Loader2, Link, Eye, X,
  Image, Check, RefreshCw, Globe, Building2
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

const DEFAULT_COLORS = {
  primary: '#02b5ff',
  secondary: '#1a1a2e',
  accent: '#ff025f'
};

export default function PartnerBranding() {
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  
  const [formData, setFormData] = useState({
    organization_name: '',
    slug: '',
    primary_color: DEFAULT_COLORS.primary,
    secondary_color: DEFAULT_COLORS.secondary,
    accent_color: DEFAULT_COLORS.accent,
    welcome_message: ''
  });

  const fetchBrand = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/whitelabel/my-brand`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.brand) {
        setBrand(response.data.brand);
        setFormData({
          organization_name: response.data.brand.organization_name || '',
          slug: response.data.brand.slug || '',
          primary_color: response.data.brand.primary_color || DEFAULT_COLORS.primary,
          secondary_color: response.data.brand.secondary_color || DEFAULT_COLORS.secondary,
          accent_color: response.data.brand.accent_color || DEFAULT_COLORS.accent,
          welcome_message: response.data.brand.welcome_message || ''
        });
      }
    } catch (err) {
      console.error('Failed to load brand:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBrand();
  }, [fetchBrand]);

  const createBrand = async () => {
    if (!formData.organization_name?.trim()) {
      toast.error('Organization name is required');
      return;
    }
    if (!formData.slug?.trim()) {
      toast.error('URL slug is required');
      return;
    }
    
    const slugPattern = /^[a-z0-9-]+$/;
    if (!slugPattern.test(formData.slug.toLowerCase())) {
      toast.error('URL slug can only contain lowercase letters, numbers, and hyphens');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/whitelabel/create`, {
        slug: formData.slug.toLowerCase().trim(),
        organization_name: formData.organization_name.trim(),
        primary_color: formData.primary_color,
        secondary_color: formData.secondary_color,
        accent_color: formData.accent_color,
        welcome_message: formData.welcome_message?.trim() || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBrand(response.data.brand);
      toast.success('Brand created successfully!');
      setShowCreateModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create brand');
    } finally {
      setSaving(false);
    }
  };

  const updateBrand = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/whitelabel/update`, {
        organization_name: formData.organization_name?.trim() || null,
        primary_color: formData.primary_color,
        secondary_color: formData.secondary_color,
        accent_color: formData.accent_color,
        welcome_message: formData.welcome_message?.trim() || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Brand updated successfully!');
      fetchBrand();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update brand');
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Use PNG, JPG, SVG, or WebP');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(`${API}/whitelabel/upload-logo`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Logo uploaded successfully!');
      fetchBrand();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const deleteBrand = async () => {
    if (!confirm('Are you sure you want to delete your branding? This cannot be undone.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/whitelabel/delete`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Brand deleted');
      setBrand(null);
      setFormData({
        organization_name: '',
        slug: '',
        primary_color: DEFAULT_COLORS.primary,
        secondary_color: DEFAULT_COLORS.secondary,
        accent_color: DEFAULT_COLORS.accent,
        welcome_message: ''
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete brand');
    }
  };

  const baseUrl = window.location.origin;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="partner-branding">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 rounded-xl flex items-center justify-center">
            <Palette className="h-5 w-5 text-indigo-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Brand Settings</h2>
            <p className="text-xs text-zinc-500">Customize your white-label experience</p>
          </div>
        </div>
        {brand && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPreviewMode(!previewMode)}
            className="border-zinc-700 text-zinc-400"
          >
            <Eye className="h-4 w-4 mr-1" />
            {previewMode ? 'Hide' : 'Show'} Preview
          </Button>
        )}
      </div>

      {!brand ? (
        /* No Brand Yet - Show Create Option */
        <div className="bg-zinc-800/50 rounded-xl p-8 border border-zinc-700/50 text-center">
          <Building2 className="h-12 w-12 mx-auto mb-4 text-indigo-400 opacity-50" />
          <h3 className="text-lg font-semibold text-white mb-2">No Branding Set Up</h3>
          <p className="text-zinc-400 mb-6 max-w-md mx-auto">
            Create your custom branded login page with your logo, colors, and unique URL.
          </p>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white"
          >
            <Palette className="h-4 w-4 mr-2" />
            Set Up Branding
          </Button>
        </div>
      ) : (
        /* Has Brand - Show Editor */
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Editor Panel */}
          <div className="space-y-6">
            {/* Logo Upload */}
            <div className="bg-zinc-800/50 rounded-xl p-6 border border-zinc-700/50">
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Image className="h-4 w-4 text-indigo-400" />
                Logo
              </h3>
              
              <div className="flex items-center gap-4">
                <div 
                  className="w-20 h-20 rounded-xl flex items-center justify-center border-2 border-dashed border-zinc-600 bg-zinc-900"
                  style={{ borderColor: brand.logo_url ? formData.primary_color : undefined }}
                >
                  {brand.logo_url ? (
                    <img 
                      src={`${API}${brand.logo_url}`} 
                      alt="Logo"
                      className="w-full h-full object-contain rounded-lg"
                    />
                  ) : (
                    <Upload className="h-8 w-8 text-zinc-500" />
                  )}
                </div>
                
                <div>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/svg+xml,image/webp"
                      onChange={uploadLogo}
                      className="hidden"
                      disabled={uploading}
                    />
                    <div className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      uploading 
                        ? 'bg-zinc-700 text-zinc-400 cursor-wait' 
                        : 'bg-indigo-500/20 text-indigo-300 hover:bg-indigo-500/30'
                    }`}>
                      {uploading ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Uploading...
                        </span>
                      ) : (
                        'Upload Logo'
                      )}
                    </div>
                  </label>
                  <p className="text-xs text-zinc-500 mt-1">PNG, JPG, SVG or WebP (max 5MB)</p>
                </div>
              </div>
            </div>

            {/* Organization Info */}
            <div className="bg-zinc-800/50 rounded-xl p-6 border border-zinc-700/50">
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Building2 className="h-4 w-4 text-indigo-400" />
                Organization
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">Organization Name</label>
                  <Input
                    value={formData.organization_name}
                    onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                    className="bg-zinc-900 border-zinc-700 text-white"
                    data-testid="brand-org-name"
                  />
                </div>
                
                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">Custom Login URL</label>
                  <div className="flex items-center gap-2 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2">
                    <Globe className="h-4 w-4 text-zinc-500" />
                    <span className="text-zinc-500 text-sm">{baseUrl}/login/</span>
                    <span className="text-indigo-400 font-medium">{brand.slug}</span>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">Share this URL with your clients</p>
                </div>
                
                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">Welcome Message (optional)</label>
                  <textarea
                    value={formData.welcome_message}
                    onChange={(e) => setFormData({ ...formData, welcome_message: e.target.value })}
                    placeholder="Welcome to our performance platform..."
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm resize-none h-20"
                    data-testid="brand-welcome-msg"
                  />
                </div>
              </div>
            </div>

            {/* Colors */}
            <div className="bg-zinc-800/50 rounded-xl p-6 border border-zinc-700/50">
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Palette className="h-4 w-4 text-indigo-400" />
                Colors
              </h3>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-zinc-400 mb-2 block">Primary</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.primary_color}
                      onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                      className="w-10 h-10 rounded-lg cursor-pointer border-0"
                    />
                    <Input
                      value={formData.primary_color}
                      onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                      className="bg-zinc-900 border-zinc-700 text-white text-xs"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-zinc-400 mb-2 block">Secondary</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.secondary_color}
                      onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                      className="w-10 h-10 rounded-lg cursor-pointer border-0"
                    />
                    <Input
                      value={formData.secondary_color}
                      onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                      className="bg-zinc-900 border-zinc-700 text-white text-xs"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-zinc-400 mb-2 block">Accent</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.accent_color}
                      onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                      className="w-10 h-10 rounded-lg cursor-pointer border-0"
                    />
                    <Input
                      value={formData.accent_color}
                      onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                      className="bg-zinc-900 border-zinc-700 text-white text-xs"
                    />
                  </div>
                </div>
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFormData({
                  ...formData,
                  primary_color: DEFAULT_COLORS.primary,
                  secondary_color: DEFAULT_COLORS.secondary,
                  accent_color: DEFAULT_COLORS.accent
                })}
                className="text-zinc-500 hover:text-white mt-4"
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                Reset to Default
              </Button>
            </div>

            {/* Save Button */}
            <div className="flex gap-3">
              <Button
                onClick={updateBrand}
                disabled={saving}
                className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-500 text-white"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save Changes
              </Button>
              <Button
                variant="outline"
                onClick={deleteBrand}
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
              >
                Delete
              </Button>
            </div>
          </div>

          {/* Preview Panel */}
          {previewMode && (
            <div className="bg-zinc-800/50 rounded-xl border border-zinc-700/50 overflow-hidden">
              <div className="p-4 border-b border-zinc-700">
                <h3 className="font-semibold text-white">Login Page Preview</h3>
              </div>
              <div 
                className="p-8 min-h-[500px]"
                style={{ backgroundColor: formData.secondary_color }}
              >
                {/* Mock Login Form */}
                <div className="max-w-sm mx-auto">
                  {/* Logo */}
                  <div className="flex justify-center mb-6">
                    {brand.logo_url ? (
                      <img 
                        src={`${API}${brand.logo_url}`} 
                        alt="Logo"
                        className="h-16 object-contain"
                      />
                    ) : (
                      <div 
                        className="w-16 h-16 rounded-xl flex items-center justify-center text-white text-2xl font-bold"
                        style={{ backgroundColor: formData.primary_color }}
                      >
                        {formData.organization_name?.charAt(0) || 'P'}
                      </div>
                    )}
                  </div>
                  
                  {/* Title */}
                  <h1 className="text-2xl font-bold text-white text-center mb-2">
                    {formData.organization_name || 'Organization'}
                  </h1>
                  
                  {/* Welcome Message */}
                  {formData.welcome_message && (
                    <p className="text-center text-zinc-400 text-sm mb-6">
                      {formData.welcome_message}
                    </p>
                  )}
                  
                  {/* Form Preview */}
                  <div className="space-y-4">
                    <div className="bg-zinc-900/50 rounded-lg px-4 py-3 border border-zinc-700">
                      <span className="text-zinc-500 text-sm">Email</span>
                    </div>
                    <div className="bg-zinc-900/50 rounded-lg px-4 py-3 border border-zinc-700">
                      <span className="text-zinc-500 text-sm">Password</span>
                    </div>
                    <button 
                      className="w-full py-3 rounded-lg font-semibold text-white transition-colors"
                      style={{ backgroundColor: formData.primary_color }}
                    >
                      Sign In
                    </button>
                  </div>
                  
                  {/* Footer */}
                  <p className="text-center text-zinc-500 text-xs mt-8">
                    Powered by BE ELITE APP
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create Brand Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" onClick={() => setShowCreateModal(false)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-md p-6 relative z-[10000]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-white">Set Up Branding</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-zinc-500 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Organization Name *</label>
                <Input
                  value={formData.organization_name}
                  onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                  placeholder="Your Organization"
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">URL Slug *</label>
                <div className="flex items-center gap-2">
                  <span className="text-zinc-500 text-sm">/login/</span>
                  <Input
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    placeholder="your-org"
                    className="bg-zinc-800 border-zinc-700 text-white flex-1"
                  />
                </div>
                <p className="text-xs text-zinc-500 mt-1">Only lowercase letters, numbers, and hyphens</p>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="flex-1 border-zinc-700"
              >
                Cancel
              </Button>
              <Button
                onClick={createBrand}
                disabled={saving || !formData.organization_name || !formData.slug}
                className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-500 text-white"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                Create
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
